import { useCallback, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Modal,
  ModalSizes,
  Form,
  FormSizes,
  Field,
  TextInput,
  Select,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyColors,
  FormValidateOn,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { useGetSupplierTransportationRoutesQuery } from '@/lib/api/lookupsApi';
import { EMPTY_LIST } from '@/lib/utils';
import {
  buildEmissionShippingOptions,
  buildEmissionRouteOptions,
} from '../utils/declarationsUtils';
import styles from './EmissionCalculationsModal.module.scss';

const numberOrZero = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

/**
 * Props:
 *   name         the form field this input writes
 *   label        the field label
 *   placeholder  the input's placeholder
 */
const DdvField = ({ name, label, placeholder }) => {
  const t = useTranslations();

  return (
    <Field name={name}>
      {({ id, name: fieldName, value, onChange, onBlur, status, message }) => (
        <div className={styles.ddvRow}>
          <TextInput
            id={id}
            name={fieldName}
            className={styles.ddvInput}
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            status={status}
            message={message}
          />
          <Button
            variant={ButtonVariants.secondary}
            size={ButtonSizes.md}
            // TODO: enable once a default-values endpoint exists — none does today.
            disabled
          >
            {t('DECLARATIONS_EMISSIONS_FETCH_DDV')}
          </Button>
        </div>
      )}
    </Field>
  );
};

/**
 * Props:
 *   emission  the tab's view model, for the five fixed components and any saved values
 *   values    the form's live values, for the running total
 *   routes    the supplier's transport routes
 */
const CalculationSummary = ({ emission, values, routes }) => {
  const t = useTranslations();

  const route = useMemo(
    () => routes.find((entry) => entry.id === String(values.routeId)),
    [routes, values.routeId],
  );

  const total = useMemo(
    () =>
      numberOrZero(values.ghgEec) +
      emission.rawGhgEl +
      numberOrZero(values.ghgEp) +
      numberOrZero(values.ghgEtd) +
      emission.rawGhgEu -
      emission.rawGhgEsca -
      emission.rawGhgEccr -
      emission.rawGhgEccs,
    [values.ghgEec, values.ghgEp, values.ghgEtd, emission],
  );

  return (
    <>
      <TextInput
        label={t('DECLARATIONS_EMISSIONS_TOTAL_TRANSPORT')}
        placeholder={t('DECLARATIONS_EMISSIONS_TOTAL_TRANSPORT_PLACEHOLDER')}
        value={route?.totalTransportationEmissions ?? ''}
        disabled
        readOnly
      />
      <TextInput
        label={t('DECLARATIONS_EMISSIONS_TOTAL')}
        placeholder={t('DECLARATIONS_EMISSIONS_TOTAL_PLACEHOLDER')}
        value={String(total)}
        disabled
        readOnly
      />
      <div className={styles.calculation}>
        <div className={styles.calculationHeader}>
          <Typography variant={TypographyVariants.caption} color={TypographyColors.placeholder}>
            {t('SECTION_CALCULATION')}
          </Typography>
          <span className={styles.calculationRule} />
        </div>
        <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.primary}>
          {t('DECLARATIONS_EMISSIONS_FORMULA')}
        </Typography>
      </div>
    </>
  );
};

/**
 * Props:
 *   open      whether the modal is shown
 *   onClose   ✕ / Cancel
 *   emission  the tab's view model — prefills Edit and supplies the fixed components
 *   isSaving  disables the primary button while the mutation is in flight
 *   onSubmit  (values) => void — runs only once validation passes
 */
export const EmissionCalculationsModal = ({ open, onClose, emission, isSaving, onSubmit }) => {
  const t = useTranslations();

  const { data: routes = EMPTY_LIST } = useGetSupplierTransportationRoutesQuery(
    emission.supplierId || skipToken,
  );

  const initialValues = useMemo(
    () => ({
      ghgEec: emission.rawGhgEec,
      ghgEp: emission.rawGhgEp,
      ghgEtd: emission.rawGhgEtd,
      shippingFromId: '',
      routeId: '',
      calorificValue: '',
    }),
    [emission],
  );

  const shippingOptions = useMemo(
    () => buildEmissionShippingOptions(routes, emission.receivedAtSiteId),
    [routes, emission.receivedAtSiteId],
  );

  const handleSubmit = useCallback(
    (values) => {
      const route = routes.find((entry) => entry.id === String(values.routeId));
      onSubmit?.({
        ghgEec: values.ghgEec,
        ghgEp: values.ghgEp,
        ghgEtd: values.ghgEtd,
        calorificValue: values.calorificValue,
        routeId: values.routeId,
        totalEmissions:
          numberOrZero(values.ghgEec) +
          emission.rawGhgEl +
          numberOrZero(values.ghgEp) +
          numberOrZero(values.ghgEtd) +
          emission.rawGhgEu -
          emission.rawGhgEsca -
          emission.rawGhgEccr -
          emission.rawGhgEccs,
        totalTransportationEmissions: route?.totalTransportationEmissions ?? null,
      });
    },
    [onSubmit, routes, emission],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={ModalSizes.large}
      title={
        emission.isEditable
          ? t('DECLARATIONS_EMISSIONS_EDIT_TITLE')
          : t('DECLARATIONS_EMISSIONS_ADD_TITLE')
      }
    >
      <Form
        validateOn={FormValidateOn.change}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        size={FormSizes.large}
      >
        {({ values }) => (
          <>
            <Form.Section>
              <DdvField
                name="ghgEec"
                label={t('FORM_EEC_EMISSIONS_OPTIONAL')}
                placeholder={t('FORM_EEC_EMISSIONS_PLACEHOLDER')}
              />

              <DdvField
                name="ghgEp"
                label={t('FORM_EP_EMISSIONS_OPTIONAL')}
                placeholder={t('FORM_EP_EMISSIONS_PLACEHOLDER')}
              />

              <DdvField
                name="ghgEtd"
                label={t('FORM_ETD_EMISSIONS_OPTIONAL')}
                placeholder={t('FORM_ETD_EMISSIONS_PLACEHOLDER')}
              />
            </Form.Section>

            <Form.Section title={t('SECTION_TRANSPORTATION_EMISSIONS')}>
              <Form.Row columns={2}>
                <Field name="shippingFromId">
                  {({ id, name, value, onChange, onBlur, status, message }) => (
                    <Select
                      id={id}
                      name={name}
                      label={t('FORM_SHIPPING_FROM_OPTIONAL')}
                      placeholder={t('FORM_SHIPPING_FROM_PLACEHOLDER')}
                      options={shippingOptions}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      status={status}
                      message={message}
                    />
                  )}
                </Field>

                <TextInput
                  label={t('FIELD_RECEIVED_AT')}
                  value={emission.receivedAtSiteName}
                  disabled
                  readOnly
                />
              </Form.Row>

              {/* Route and calorific value appear only once a shipping site is chosen. */}
              {values.shippingFromId && (
                <Field name="routeId">
                  {({ id, name, value, onChange, onBlur, status, message }) => (
                    <Select
                      id={id}
                      name={name}
                      label={t('FORM_SELECT_ROUTE_OPTIONAL')}
                      placeholder={t('COMMON_SELECT')}
                      options={buildEmissionRouteOptions(routes, values.shippingFromId)}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      status={status}
                      message={message}
                    />
                  )}
                </Field>
              )}

              {values.routeId && (
                <Field name="calorificValue" required>
                  {({ id, name, value, onChange, onBlur, status, message, required }) => (
                    <TextInput
                      id={id}
                      name={name}
                      label={t('FORM_CALORIFIC_VALUE')}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      status={status}
                      message={message}
                      required={required}
                    />
                  )}
                </Field>
              )}

              <CalculationSummary emission={emission} values={values} routes={routes} />
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={onClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSaving}
              >
                {t('COMMON_ADD')}
              </Button>
            </Form.Footer>
          </>
        )}
      </Form>
    </Modal>
  );
};
